import { Table, TH, TD, TR } from '@ag-media/react-pdf-table';
import { Document, View, Image } from '@react-pdf/renderer';
import { PatientInfoState } from 'features/patient/_utils/patient-info/patient-info-slice-types';
import {
  brainFunctionVisualisations_new,
  brainFunctionCategories
} from 'lib/utils/business/brainfunction-categories';
import { pluralize } from 'lib/utils/string/normalise';
import { PDFBrainFunctionCategory } from './components/pdf-brainfunction-category';
import { ColorLegend } from './components/pdf-brainfunction-legend';
import { PDFConcussionClassificationAndCriticalAreas } from './components/pdf-concussionClassification-and-criticalAreas';
import {
  CRxPDFPage,
  H1,
  H3,
  BodyText,
  H2
} from './components/pdf-default-components';
import { ReportOverview } from './components/pdf-report-overview';
import { PDFSymptomCard } from './components/pdf-symptom-card';
import { SERIALISE_CONCUSSION_COMPLEXITY_SCALE_CLASSNAME } from './components/serialise-svg-component';
import { UseImageStoreReturn } from './utils/create-image-store';

export function PatientDashboardReport(props: {
  patientInfo: PatientInfoState;
  imageStore: Pick<UseImageStoreReturn, 'getImage'>;
}) {
  const name = props.patientInfo.patient.displayName;
  const currentDate = new Date();
  return (
    <Document
      style={{
        fontSize: 12
      }}
    >
      <CRxPDFPage
        style={{
          padding: 12
        }}
        header={{
          date: currentDate,
          diplayName: name
        }}
      >
        <View>
          <H1>ConcussionRx Summary - {name}</H1>
          <View
            style={{
              gap: 24
            }}
          >
            <ReportOverview patientInfo={props.patientInfo} />
            {
              /**
               * Only display the concussion visualisations if there is a concussion
               */
              props.patientInfo.concussion && (
                <>
                  <View
                    style={{
                      flexDirection: 'row',
                      width: '100%',
                      gap: 16
                    }}
                  >
                    <View
                      style={{
                        width: '50%'
                      }}
                    >
                      <PDFConcussionClassificationAndCriticalAreas
                        patientInfo={props.patientInfo}
                      />
                    </View>

                    <View
                      style={{
                        width: '50%'
                      }}
                    >
                      <Image
                        src={
                          props.imageStore.getImage(
                            SERIALISE_CONCUSSION_COMPLEXITY_SCALE_CLASSNAME
                          ) ?? ''
                        }
                        style={{
                          width: '100%'
                        }}
                      />
                    </View>
                  </View>

                  <View>
                    <H3>Metrics</H3>
                    <Table
                      tdStyle={{
                        border: '0px solid black',
                        gap: 16
                      }}
                    >
                      <TH>
                        <TD>
                          <BodyText>Total Symptom Score Tracker</BodyText>
                        </TD>
                        <TD>
                          <BodyText>Concussion Complexity Over time</BodyText>
                        </TD>
                      </TH>
                      <TR>
                        <TD>
                          <Image
                            src={
                              props.imageStore.getImage(
                                'metrics-total-symptom-score-tracker'
                              ) ?? ''
                            }
                            style={{
                              width: '100%'
                            }}
                          />
                        </TD>
                        <TD>
                          <Image
                            src={
                              props.imageStore.getImage(
                                'metrics-concussion-complexity-over-time'
                              ) ?? ''
                            }
                            style={{
                              width: '100%'
                            }}
                          />
                        </TD>
                      </TR>
                    </Table>
                  </View>
                </>
              )
            }
          </View>
        </View>
      </CRxPDFPage>
      <CRxPDFPage
        style={{
          padding: 12
        }}
        header={{
          date: currentDate,
          diplayName: name
        }}
      >
        <H1>{pluralize(name)} Brain on Concussion</H1>
        <View
          style={{
            gap: 16
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              gap: 16
            }}
          >
            <View
              style={{
                flex: 2
              }}
            >
              <BodyText>
                Understanding {pluralize(name)} concussion and its impact on
                critical functional systems.
              </BodyText>
              <BodyText>
                These are the foundation for the recommendations to follow.
              </BodyText>
            </View>

            <View style={{ flex: 1 }}>
              <ColorLegend />
            </View>
          </View>
          <View
            style={{
              gap: 16
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                justifyContent: 'center'
              }}
            >
              {brainFunctionVisualisations_new.highLevel.map(_ => (
                <PDFSymptomCard
                  key={_.label}
                  title={_.label}
                  gaugeImage={
                    props.imageStore.getImage(
                      ['brainfunction', _.label, 'gauge'].join('-')
                    ) ?? ''
                  }
                  scatterImage={
                    props.imageStore.getImage(
                      ['brainfunction', _.label, 'scatter'].join('-')
                    ) ?? ''
                  }
                />
              ))}
            </View>
            <View
              style={{
                flexDirection: 'row',
                gap: 16,
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}
            >
              {brainFunctionVisualisations_new.rest.map(_ => (
                <PDFSymptomCard
                  key={_.label}
                  title={_.label}
                  gaugeImage={
                    props.imageStore.getImage(
                      ['brainfunction', _.label, 'gauge'].join('-')
                    ) ?? ''
                  }
                  scatterImage={
                    props.imageStore.getImage(
                      ['brainfunction', _.label, 'scatter'].join('-')
                    ) ?? ''
                  }
                />
              ))}
            </View>
          </View>
        </View>
      </CRxPDFPage>

      {brainFunctionCategories.map((category, i) => (
        <CRxPDFPage
          key={category.title}
          header={{
            date: currentDate,
            diplayName: name
          }}
        >
          <View>
            {i === 0 && (
              <View>
                <H2>Functional Overview</H2>
              </View>
            )}
            <PDFBrainFunctionCategory
              imageStore={props.imageStore}
              brainfunctionCategory={category}
            />
          </View>
        </CRxPDFPage>
      ))}
    </Document>
  );
}
